import React, { useState, useEffect } from "react";
import {
	Calendar,
	ChevronDown,
	Loader2,
	Info,
	Upload,
	FileText,
	XCircle,
	Clock,
	CheckCircle,
} from "lucide-react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useAuth } from "./AuthContext";
import { useTranslation } from "../i18n/TranslationContext";
import {
	useGetCountries,
	useUpdateExtra,
	useGetOccupations,
	useGetExtra,
} from "../api";

import { useOccupationProof } from "../api/occupationProof/useOccupationProof";
import { LANGUAGE_LEVELS } from "../constants/languageLevels";
import { useDataAccuracyConfirmation } from "../hooks/useDataAccuracyConfirmation";
import { useWorkPermitProof } from "../api/workPermitProof/useWorkPermitProof";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { COMPANY_OPTIONS } from "../constants/companies";
import { AUSWEIS_COLOR_OPTIONS } from "../constants/ausweisColors";
import type { LanguageLevel } from "../types/extra";
import { toLocalDateString } from "../utils";

export function ProfileData() {
	const {
		userProfile,
		setUserProfile,
		updateProcessState,
		navigateDirectly,
		triggerAutomaticBooking,
	} = useApp();
	const { authData } = useAuth();
	const { t } = useTranslation();
	const { countries, loading: countriesLoading } = useGetCountries();
	const { occupations, loading: occupationsLoading } = useGetOccupations();
	const { user } = useCurrentUser();
	const { extra, loading: extraLoading, refetch } = useGetExtra(user?.extraId);
	const { updateExtra, loading: updateLoading } = useUpdateExtra();
	const { proofInfo, deleteProof } = useOccupationProof(user?.extraId);
	const { proofInfo: workPermitInfo, deleteProof: deleteWorkPermitProof } =
		useWorkPermitProof(user?.extraId);

	// Use countries from backend, sorted alphabetically
	const sortedCountries = [...countries].sort((a, b) =>
		a.nationality.localeCompare(b.nationality)
	);

	// Use actual enum values, not translations, to match backend expectations
	const germanLevels = LANGUAGE_LEVELS;
	const [formData, setFormData] = useState({
		email: userProfile?.email || extra?.email || "",
		firstName: userProfile?.firstName || extra?.first_name || "",
		lastName: userProfile?.lastName || extra?.last_name || "",
		phone: userProfile?.phone || extra?.mobile_number || "",
		dateOfBirth:
			userProfile?.dateOfBirth ||
			(extra?.date_of_birth ? toLocalDateString(extra.date_of_birth) : ""),
		nationality:
			userProfile?.nationality ||
			(extra?.nationality
				? countries.find((c) => c.country_id === extra.nationality)?.nationality
				: "") ||
			"",
		germanLevel:
			userProfile?.germanLevel ||
			authData.germanLevel ||
			extra?.language_level ||
			"",
		street: userProfile?.street || extra?.street || "",
		postalCode: userProfile?.postalCode || extra?.postcode || "",
		city: userProfile?.city || extra?.city || "",
		cityOfBirth: userProfile?.cityOfBirth || extra?.city_of_birth || "",
		company: userProfile?.company || extra?.company || "",
		department: userProfile?.department || extra?.department || "",
		flughafenausweisnummer:
			userProfile?.flughafenausweisnummer ||
			extra?.flughafenausweisnummer ||
			"",
		ausweis_color: userProfile?.ausweis_color || extra?.ausweis_color || "",
		mainOccupationId: extra?.main_occupation_id || "",
		id_number: userProfile?.id_number || extra?.id_number || "",
		accepts_64_day_rule:
			userProfile?.accepts_64_day_rule || extra?.accepts_64_day_rule || false,
	});

	const [validationError, setValidationError] = useState<string>("");
	const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>(
		{}
	);
	// Navigation warning modal for unsaved changes
	const [showNavigationWarningModal, setShowNavigationWarningModal] =
		useState(false);
	const [navigationTarget, setNavigationTarget] = useState<
		"occupation-proof" | "work-permit-proof" | null
	>(null);
	// Confirmation modal for occupation change
	const [showOccupationChangeModal, setShowOccupationChangeModal] =
		useState(false);
	const [pendingOccupationId, setPendingOccupationId] = useState<string>("");
	// Confirmation modal for nationality change (work permit)
	const [showNationalityChangeModal, setShowNationalityChangeModal] =
		useState(false);
	const [pendingNationality, setPendingNationality] = useState<string>("");
	// Info modal for ID number explanation
	const [showIdInfoModal, setShowIdInfoModal] = useState(false);
	// Data accuracy acknowledgment with localStorage persistence
	const { acknowledgesDataAccuracy, setAcknowledgesDataAccuracy } =
		useDataAccuracyConfirmation("profile", user?.extraId);

	// Update form when extra data is loaded
	useEffect(() => {
		if (extra && !extraLoading) {
			setFormData({
				email: extra.email || "",
				firstName: extra.first_name || "",
				lastName: extra.last_name || "",
				phone: extra.mobile_number || "",
				dateOfBirth: extra.date_of_birth
					? toLocalDateString(extra.date_of_birth)
					: "",
				nationality: extra.nationality
					? countries.find((c) => c.country_id === extra.nationality)
							?.nationality || ""
					: "",
				germanLevel: extra.language_level || "",
				street: extra.street || "",
				postalCode: extra.postcode || "",
				city: extra.city || "",
				cityOfBirth: extra.city_of_birth || "",
				company: extra.company || "",
				department: extra.department || "",
				flughafenausweisnummer: extra.flughafenausweisnummer || "",
				ausweis_color: extra.ausweis_color || "",
				mainOccupationId: extra.main_occupation_id?.toString() || "",
				id_number: extra.id_number || "",
				accepts_64_day_rule: extra.accepts_64_day_rule || false,
			});
		}
	}, [extra, extraLoading, countries]);

	// Check if occupation proof upload should be shown (only for students)
	const shouldShowOccupationProof = () => {
		if (!formData.mainOccupationId) return false;

		// Get the selected occupation
		const selectedOccupation = occupations.find(
			(occ) => occ.id.toString() === formData.mainOccupationId
		);

		// Show occupation proof for students and high school students only
		if (selectedOccupation?.name) {
			const occupationName = selectedOccupation.name.toLowerCase();
			return (
				occupationName.includes("student") || occupationName.includes("schüler")
			);
		}

		return false;
	};

	// Check if there are unsaved changes
	const hasUnsavedChanges = () => {
		if (!extra) return false;

		// Compare key fields that might have changed
		const fieldsToCheck = [
			"firstName",
			"lastName",
			"phone",
			"dateOfBirth",
			"street",
			"postalCode",
			"city",
			"cityOfBirth",
			"company",
			"department",
			"flughafenausweisnummer",
			"ausweis_color",
		];

		// Map form field names to extra field names
		const fieldMapping: { [key: string]: string } = {
			firstName: "first_name",
			lastName: "last_name",
			phone: "mobile_number",
			dateOfBirth: "date_of_birth",
			postalCode: "postcode",
			cityOfBirth: "city_of_birth",
		};

		for (const field of fieldsToCheck) {
			const currentValue = formData[field as keyof typeof formData];
			const extraField = fieldMapping[field] || field;
			const originalValue = extra[extraField as keyof typeof extra];

			// Handle date formatting comparison
			if (field === "dateOfBirth") {
				const currentDate = currentValue || "";
				const originalDate = originalValue
					? toLocalDateString(originalValue as string)
					: "";
				if (currentDate !== originalDate) {
					return true;
				}
			} else {
				// Handle null/undefined comparisons
				const normalizedCurrent =
					currentValue === null || currentValue === undefined
						? ""
						: currentValue;
				const normalizedOriginal =
					originalValue === null || originalValue === undefined
						? ""
						: originalValue;

				if (normalizedCurrent !== normalizedOriginal) {
					return true;
				}
			}
		}

		// Check nationality separately
		const currentNat = formData.nationality || "";
		const originalNat = extra.nationality
			? countries.find((c) => c.country_id === extra.nationality)
					?.nationality || ""
			: "";
		if (currentNat !== originalNat) {
			return true;
		}

		// Check occupation
		const currentOcc = formData.mainOccupationId?.toString() || "";
		const originalOcc = extra.main_occupation_id?.toString() || "";
		if (currentOcc !== originalOcc) {
			return true;
		}

		// Check german level
		const currentLevel = formData.germanLevel || "";
		const originalLevel = extra.language_level || "";
		if (currentLevel !== originalLevel) {
			return true;
		}

		return false;
	};

	// Navigate to occupation proof
	const navigateToOccupationProof = () => {
		if (hasUnsavedChanges()) {
			setNavigationTarget("occupation-proof");
			setShowNavigationWarningModal(true);
		} else if (formData.mainOccupationId) {
			navigateDirectly("occupation-proof");
		}
	};

	// Check if work permit upload should be shown (only for non-EU nationals)
	const shouldShowWorkPermitProof = () => {
		if (!formData.nationality) return false;

		// Get the selected country
		const selectedCountry = countries.find(
			(country) => country.nationality === formData.nationality
		);

		// Show work permit proof for non-EU nationals only
		return selectedCountry && !selectedCountry.is_eu;
	};

	// Navigate to work permit proof
	const navigateToWorkPermitProof = () => {
		if (hasUnsavedChanges()) {
			setNavigationTarget("work-permit-proof");
			setShowNavigationWarningModal(true);
		} else if (formData.nationality) {
			navigateDirectly("work-permit-proof");
		}
	};

	// Handle save and navigate
	const handleSaveAndNavigate = async () => {
		if (validateForm()) {
			// Clear errors
			setValidationError("");
			setFieldErrors({});

			if (!user?.extraId) {
				console.error("User extraId is not set");
				setShowNavigationWarningModal(false);
				return;
			}

			// Prepare data for API - map camelCase to snake_case
			// Convert nationality string to country_id
			const nationalityId = formData.nationality
				? countries.find((c) => c.nationality === formData.nationality)
						?.country_id
				: undefined;

			const dataToSubmit = {
				first_name: formData.firstName,
				last_name: formData.lastName,
				date_of_birth: formData.dateOfBirth,
				nationality: nationalityId,
				language_level: formData.germanLevel as LanguageLevel,
				street: formData.street,
				postcode: formData.postalCode,
				city: formData.city,
				city_of_birth: formData.cityOfBirth,
				company: formData.company,
				department: formData.department,
				flughafenausweisnummer: formData.flughafenausweisnummer,
				ausweis_color: formData.ausweis_color,
				main_occupation_id: formData.mainOccupationId
					? Number(formData.mainOccupationId)
					: undefined,
				id_number: formData.id_number,
				accepts_64_day_rule: formData.accepts_64_day_rule,
			};

			// Send data to API
			const updatedExtra = await updateExtra(user.extraId, dataToSubmit);

			if (updatedExtra) {
				// Update local state
				if (userProfile) {
					setUserProfile({ ...userProfile, ...formData });
				}

				// Close modal and navigate
				setShowNavigationWarningModal(false);

				// Navigate based on target
				if (navigationTarget === "work-permit-proof") {
					navigateDirectly("work-permit-proof");
				} else if (navigationTarget === "occupation-proof") {
					navigateDirectly("occupation-proof");
				}
				// If navigationTarget is null, we don't navigate anywhere
			}
		} else {
			setShowNavigationWarningModal(false);
		}
	};

	const handleInputChange = (field: string, value: string | boolean) => {
		// Special handling for occupation changes when proof exists
		if (
			field === "mainOccupationId" &&
			proofInfo &&
			typeof value === "string" &&
			value !== formData.mainOccupationId
		) {
			// If changing occupation and there's existing proof, show warning
			setPendingOccupationId(value);
			setShowOccupationChangeModal(true);
			return;
		}

		// Special handling for nationality changes when work permit exists
		if (
			field === "nationality" &&
			workPermitInfo &&
			typeof value === "string" &&
			value !== formData.nationality
		) {
			const newCountry = countries.find((c) => c.nationality === value);
			const oldCountry = countries.find(
				(c) => c.nationality === formData.nationality
			);

			// Check if changing from non-EU to EU or vice versa
			if (newCountry && oldCountry && newCountry.is_eu !== oldCountry.is_eu) {
				setPendingNationality(value);
				setShowNationalityChangeModal(true);
				return;
			}
		}

		setFormData((prev) => ({ ...prev, [field]: value }));

		// Clear field error when user starts typing
		if (fieldErrors[field]) {
			setFieldErrors((prev) => ({ ...prev, [field]: false }));
		}

		// Clear validation error if all fields are now valid
		if (validationError) {
			const updatedFormData = { ...formData, [field]: value };
			if (validateForm(updatedFormData)) {
				setValidationError("");
			}
		}
	};

	const validateForm = (data = formData) => {
		const requiredFields = [
			"firstName",
			"lastName",
			"phone",
			"dateOfBirth",
			"nationality",
			"germanLevel",
			"street",
			"postalCode",
			"city",
			"cityOfBirth",
			"accepts64DayRule",
		];

		// Add internal-only fields if user is internal
		if (user?.internal_extra) {
			requiredFields.push("flughafenausweisnummer", "ausweis_color");
		} else {
			// Add fields required for external users
			requiredFields.push("mainOccupationId", "idNumber");
		}

		const allFieldsFilled = requiredFields.every(
			(field) =>
				data[field as keyof typeof data] &&
				data[field as keyof typeof data].toString().trim() !== ""
		);

		// Additional validation for date of birth (pure string comparison, no Date parsing)
		if (allFieldsFilled && data.dateOfBirth) {
			const todayStr = toLocalDateString(new Date());
			if (data.dateOfBirth > todayStr) {
				return false; // Date of birth cannot be in the future
			}
		}

		return allFieldsFilled;
	};

	// Handle confirmed occupation change
	const handleConfirmOccupationChange = async () => {
		// Delete the existing proof if it exists
		if (proofInfo) {
			try {
				await deleteProof();
				console.log("Occupation proof deleted due to occupation change");
			} catch (error) {
				console.error("Failed to delete occupation proof:", error);
				// Continue with occupation change even if proof deletion fails
			}
		}

		// Update the occupation
		setFormData((prev) => ({ ...prev, mainOccupationId: pendingOccupationId }));
		setShowOccupationChangeModal(false);
		setPendingOccupationId("");

		// Clear any field errors
		if (fieldErrors.mainOccupationId) {
			setFieldErrors((prev) => ({ ...prev, mainOccupationId: false }));
		}
	};

	// Handle cancelled occupation change
	const handleCancelOccupationChange = () => {
		setShowOccupationChangeModal(false);
		setPendingOccupationId("");
	};

	// Handle confirmed nationality change
	const handleConfirmNationalityChange = async () => {
		// Delete the existing work permit if it exists
		if (workPermitInfo) {
			try {
				await deleteWorkPermitProof();
				console.log("Work permit proof deleted due to nationality change");
			} catch (error) {
				console.error("Failed to delete work permit proof:", error);
				// Continue with nationality change even if proof deletion fails
			}
		}

		// Update the nationality
		setFormData((prev) => ({ ...prev, nationality: pendingNationality }));
		setShowNationalityChangeModal(false);
		setPendingNationality("");

		// Clear any field errors
		if (fieldErrors.nationality) {
			setFieldErrors((prev) => ({ ...prev, nationality: false }));
		}
	};

	// Handle cancelled nationality change
	const handleCancelNationalityChange = () => {
		setShowNationalityChangeModal(false);
		setPendingNationality("");
	};

	const highlightEmptyFields = () => {
		const requiredFields = [
			"firstName",
			"lastName",
			"phone",
			"dateOfBirth",
			"nationality",
			"germanLevel",
			"idNumber",
			"street",
			"postalCode",
			"city",
			"cityOfBirth",
			"accepts64DayRule",
		];

		// Add internal-only fields if user is internal
		if (user?.internal_extra) {
			requiredFields.push("flughafenausweisnummer", "ausweis_color");
		} else {
			// Add occupation for external users
			requiredFields.push("mainOccupationId");
		}

		const newFieldErrors: { [key: string]: boolean } = {};

		requiredFields.forEach((field) => {
			if (
				!formData[field as keyof typeof formData] ||
				formData[field as keyof typeof formData].toString().trim() === ""
			) {
				newFieldErrors[field] = true;
			}
		});

		setFieldErrors(newFieldErrors);
	};

	const handleSave = async () => {
		if (validateForm()) {
			// Clear errors
			setValidationError("");
			setFieldErrors({});

			if (!user?.extraId) {
				console.error("User extraId is not set");
				return;
			}

			// Prepare data for API - map camelCase to snake_case
			// Convert nationality string to country_id
			const nationalityId = formData.nationality
				? countries.find((c) => c.nationality === formData.nationality)
						?.country_id
				: undefined;

			const dataToSubmit = {
				first_name: formData.firstName,
				last_name: formData.lastName,
				date_of_birth: formData.dateOfBirth,
				nationality: nationalityId,
				language_level: formData.germanLevel as LanguageLevel,
				street: formData.street,
				postcode: formData.postalCode,
				city: formData.city,
				city_of_birth: formData.cityOfBirth,
				company: formData.company,
				department: formData.department,
				flughafenausweisnummer: formData.flughafenausweisnummer,
				ausweis_color: formData.ausweis_color,
				main_occupation_id: formData.mainOccupationId
					? Number(formData.mainOccupationId)
					: undefined,
				id_number: formData.id_number,
				accepts_64_day_rule: formData.accepts_64_day_rule,
			};

			// Send data to API
			const updatedExtra = await updateExtra(user.extraId, dataToSubmit);

			if (updatedExtra) {
				// Update local state
				if (userProfile) {
					setUserProfile({ ...userProfile, ...formData });
				}
				// Mark Step 3 complete, trigger automatic booking, and navigate to Step 4 (Dashboard/Job Cards)
				updateProcessState({ profileDataCompleted: true });
				triggerAutomaticBooking();
				navigateDirectly("dashboard");
			}
		} else {
			// Check if date of birth is in the future
			if (formData.dateOfBirth) {
				const selectedDate = new Date(formData.dateOfBirth);
				const today = new Date();
				today.setHours(0, 0, 0, 0);

				if (selectedDate > today) {
					setValidationError(t("profileData.validation.dateOfBirthFuture"));
					setFieldErrors({ dateOfBirth: true });
					return;
				}
			}

			setValidationError(t("profileData.validation.allFieldsRequired"));
			highlightEmptyFields();
		}
	};

	const handleBack = () => {
		navigateDirectly("financial-data");
	};

	return (
		<>
			{/* Occupation Change Confirmation Modal */}
			{showOccupationChangeModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Beschäftigung ändern
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Wenn Sie Ihre Beschäftigung ändern, wird Ihr vorhandener
							Beschäftigungsnachweis gelöscht. Sie müssen einen neuen Nachweis
							für den ausgewählten Beschäftigung hochladen.
						</p>
						<div className="flex gap-3">
							<button
								onClick={handleCancelOccupationChange}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleConfirmOccupationChange}
								className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Änderung bestätigen
							</button>
						</div>
					</div>
				</div>
			)}

			<MainLayout showBack onBack={handleBack} showBottomNav={false}>
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
						{t("profileData.title")}
					</h1>
					<p className="text-[var(--ucm-text-medium)]">
						{t("profileData.subtitle")}
					</p>
				</div>

				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
					<div className="space-y-6">
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.emailAddress")}
							</label>
							<input
								type="email"
								value={formData.email}
								className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)]"
								disabled
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.firstName")}
							</label>
							<input
								type="text"
								value={formData.firstName}
								onChange={(e) => handleInputChange("firstName", e.target.value)}
								className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									fieldErrors.firstName
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.lastName")}
							</label>
							<input
								type="text"
								value={formData.lastName}
								onChange={(e) => handleInputChange("lastName", e.target.value)}
								className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									fieldErrors.lastName
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.phoneNumber")}
							</label>
							<div className="relative">
								<input
									type="tel"
									value={formData.phone}
									onChange={(e) => handleInputChange("phone", e.target.value)}
									className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
										fieldErrors.phone
											? "border-[var(--ucm-error-red)]"
											: "border-[var(--ucm-border-light)]"
									}`}
								/>
								<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5" />
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.dateOfBirth")}
							</label>
							<div className="relative">
								<input
									type="date"
									value={formData.dateOfBirth}
									min="1900-01-01"
									max={toLocalDateString(new Date())} // Prevent selecting future dates
									onChange={(e) =>
										handleInputChange("dateOfBirth", e.target.value)
									}
									className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
										fieldErrors.dateOfBirth
											? "border-[var(--ucm-error-red)]"
											: "border-[var(--ucm-border-light)]"
									}`}
								/>
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.nationality")}
							</label>
							<div className="relative">
								{countriesLoading ? (
									<div className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-white flex items-center justify-center">
										<Loader2 className="w-5 h-5 animate-spin text-[var(--ucm-primary-teal)]" />
									</div>
								) : (
									<select
										value={formData.nationality}
										onChange={(e) =>
											handleInputChange("nationality", e.target.value)
										}
										className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
											fieldErrors.nationality
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
									>
										<option value="">
											{t("profileData.selectNationality")}
										</option>
										{sortedCountries.map((country) => (
											<option
												key={country.country_id}
												value={country.nationality}
											>
												{country.nationality}
											</option>
										))}
									</select>
								)}
								{!countriesLoading && (
									<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
								)}
							</div>
						</div>

						{/* ID-Nummer - Only for external users */}
						{user?.internal_extra !== true && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									ID-Nummer <span className="text-red-500">*</span>
								</label>
								<div className="relative">
									<input
										type="text"
										value={formData.id_number}
										onChange={(e) =>
											handleInputChange("idNumber", e.target.value)
										}
										className={`w-full h-14 px-4 pr-10 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
											fieldErrors.idNumber
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
										placeholder="Personalausweis- oder Reisepassnummer"
										maxLength={50}
									/>
									<Info
										className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ucm-text-light)] cursor-pointer hover:text-[var(--ucm-primary-teal)] transition-colors"
										onClick={() => setShowIdInfoModal(true)}
									/>
								</div>
							</div>
						)}

						{/* Work permit proof button - Only for non-EU nationals */}
						{user?.internal_extra !== true && shouldShowWorkPermitProof() && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									Arbeitserlaubnis
								</label>
								<div
									onClick={navigateToWorkPermitProof}
									className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl cursor-pointer hover:border-[var(--ucm-primary-teal)] transition-colors flex items-center justify-between group"
								>
									<div className="flex items-center gap-3">
										{workPermitInfo ? (
											<>
												<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
												<span className="text-[var(--ucm-text-dark)]">
													{workPermitInfo.file_name}
												</span>
											</>
										) : (
											<>
												<Upload className="w-5 h-5 text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]" />
												<span className="text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]">
													Arbeitserlaubnis hochladen
												</span>
											</>
										)}
									</div>
									<ChevronDown className="w-5 h-5 text-[var(--ucm-text-medium)] transform -rotate-90" />
								</div>
								{!workPermitInfo ? (
									<p className="mt-2 text-xs text-[var(--ucm-text-light)]">
										Als Nicht-EU-Bürger benötigen wir Ihre Arbeitserlaubnis.
										Bitte laden Sie den Nachweis hoch, um sich auf Events
										bewerben zu können.
									</p>
								) : (
									<div
										className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
											workPermitInfo.status === "pending"
												? "bg-yellow-100 text-yellow-800"
												: workPermitInfo.status === "approved"
													? "bg-green-100 text-green-800"
													: "bg-red-100 text-red-800"
										}`}
									>
										{workPermitInfo.status === "approved" && (
											<CheckCircle className="w-3 h-3 text-green-800" />
										)}

										{workPermitInfo.status === "pending" && (
											<Clock className="w-3 h-3 text-yellow-800" />
										)}
										{workPermitInfo.status === "rejected" && (
											<XCircle className="w-3 h-3 text-red-800" />
										)}

										{workPermitInfo.status === "pending" && "Ausstehend"}
										{workPermitInfo.status === "approved" && "Genehmigt"}
										{workPermitInfo.status === "rejected" && "Abgelehnt"}
									</div>
								)}
							</div>
						)}

						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.germanLevel")}
							</label>
							<div className="relative">
								<select
									value={formData.germanLevel}
									onChange={(e) =>
										handleInputChange("germanLevel", e.target.value)
									}
									className="w-full h-14 px-4 pr-12 border border-[var(--ucm-border-light)] rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
								>
									{germanLevels.map((level) => (
										<option key={level} value={level}>
											{level}
										</option>
									))}
								</select>
								<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
							</div>
						</div>

						{/* Company - Only show for internal users */}
						{user?.internal_extra && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									{t("profileData.fields.company")}
								</label>
								<div className="relative">
									<select
										value={formData.company}
										onChange={(e) =>
											handleInputChange("company", e.target.value)
										}
										className="w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent border-[var(--ucm-border-light)] appearance-none pr-10"
									>
										<option value="">
											{t("profileData.fields.companyPlaceholder")}
										</option>
										{COMPANY_OPTIONS.map((company) => (
											<option key={company} value={company}>
												{company}
											</option>
										))}
									</select>
									<ChevronDown
										className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
										size={20}
									/>
								</div>
							</div>
						)}

						{/* Department - Only show for internal users */}
						{user?.internal_extra && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									{t("profileData.fields.department")}
								</label>
								<input
									type="text"
									value={formData.department}
									onChange={(e) =>
										handleInputChange("department", e.target.value)
									}
									className="w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent border-[var(--ucm-border-light)]"
									placeholder={t("profileData.fields.departmentPlaceholder")}
								/>
							</div>
						)}

						{/* Airport ID - Only show for internal users */}
						{user?.internal_extra && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									{t("profileData.fields.flughafenausweisnummer")}{" "}
									<span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									value={formData.flughafenausweisnummer}
									onChange={(e) =>
										handleInputChange("flughafenausweisnummer", e.target.value)
									}
									className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
										fieldErrors.flughafenausweisnummer
											? "border-red-500"
											: "border-[var(--ucm-border-light)]"
									}`}
									placeholder={t(
										"profileData.fields.flughafenausweisnummerPlaceholder"
									)}
									maxLength={50}
								/>
							</div>
						)}

						{/* Ausweis Color - Only show for internal users */}
						{user?.internal_extra && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									{t("profileData.fields.ausweisColor")}{" "}
									<span className="text-red-500">*</span>
								</label>
								<div className="relative">
									<select
										value={formData.ausweis_color}
										onChange={(e) =>
											handleInputChange("ausweis_color", e.target.value)
										}
										className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent appearance-none pr-10 ${
											fieldErrors.ausweis_color
												? "border-red-500"
												: "border-[var(--ucm-border-light)]"
										}`}
									>
										<option value="">
											{t("profileData.fields.ausweisColorPlaceholder")}
										</option>
										{AUSWEIS_COLOR_OPTIONS.map((color) => (
											<option key={color} value={color}>
												{color}
											</option>
										))}
									</select>
									<ChevronDown
										className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
										size={20}
									/>
								</div>
							</div>
						)}

						{/* Occupation - Only show for external users */}
						{!user?.internal_extra && (
							<div>
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									{t("profileData.fields.occupation")}{" "}
									<span className="text-red-500">*</span>
								</label>
								<div className="relative">
									{occupationsLoading ? (
										<div className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-white flex items-center justify-center">
											<Loader2 className="w-5 h-5 animate-spin text-[var(--ucm-primary-teal)]" />
										</div>
									) : (
										<select
											value={formData.mainOccupationId}
											onChange={(e) =>
												handleInputChange("mainOccupationId", e.target.value)
											}
											className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
												fieldErrors.mainOccupationId
													? "border-[var(--ucm-error-red)]"
													: "border-[var(--ucm-border-light)]"
											}`}
										>
											<option value="">
												{t("profileData.fields.occupationPlaceholder")}
											</option>
											{occupations.map((occupation) => (
												<option key={occupation.id} value={occupation.id}>
													{occupation.name}
												</option>
											))}
										</select>
									)}
									{!occupationsLoading && (
										<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
									)}
								</div>
							</div>
						)}

						{/* Occupation proof button - Only for students */}
						{user?.internal_extra !== true && shouldShowOccupationProof() && (
							<div className="mt-4">
								<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
									{t("profileCompletion.occupationProof")}
								</label>
								<div
									onClick={navigateToOccupationProof}
									className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl cursor-pointer hover:border-[var(--ucm-primary-teal)] transition-colors flex items-center justify-between group"
								>
									<div className="flex items-center gap-3">
										{proofInfo ? (
											<>
												<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
												<span className="text-[var(--ucm-text-dark)]">
													{proofInfo.file_name}
												</span>
											</>
										) : (
											<>
												<Upload className="w-5 h-5 text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]" />
												<span className="text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]">
													{t("profileCompletion.uploadOccupationProof")}
												</span>
											</>
										)}
									</div>
									<ChevronDown className="w-5 h-5 text-[var(--ucm-text-medium)] transform -rotate-90" />
								</div>
								{!proofInfo ? (
									<p className="mt-2 text-xs text-[var(--ucm-text-light)]">
										Bitte laden Sie den Nachweis hoch, um Termine auswählen zu
										können.
									</p>
								) : (
									<div
										className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
											proofInfo.status === "pending"
												? "bg-yellow-100 text-yellow-800"
												: proofInfo.status === "approved"
													? "bg-green-100 text-green-800"
													: "bg-red-100 text-red-800"
										}`}
									>
										{proofInfo.status === "approved" && (
											<CheckCircle className="w-3 h-3 text-green-800" />
										)}

										{proofInfo.status === "pending" && (
											<Clock className="w-3 h-3 text-yellow-800" />
										)}
										{proofInfo.status === "rejected" && (
											<XCircle className="w-3 h-3 text-red-800" />
										)}

										{proofInfo.status === "pending" && "Ausstehend"}
										{proofInfo.status === "approved" && "Genehmigt"}
										{proofInfo.status === "rejected" && "Abgelehnt"}
									</div>
								)}
							</div>
						)}
					</div>

					{/* Validation Error */}
					{validationError && (
						<div className="bg-[var(--ucm-error-red)] bg-opacity-10 border border-[var(--ucm-error-red)] rounded-xl p-3 mt-6">
							<p className="text-[var(--ucm-error-red)] text-sm text-center">
								{validationError}
							</p>
						</div>
					)}

					{/* Agreement Checkboxes - Only for external users */}
					{user?.internal_extra !== true && (
						<div className="space-y-4 mb-6">
							<div className="flex items-center space-x-3">
								<input
									type="checkbox"
									id="accepts64DayRule"
									checked={formData.accepts_64_day_rule || false}
									onChange={(e) =>
										handleInputChange("accepts_64_day_rule", e.target.checked)
									}
									className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
								/>
								<label
									htmlFor="accepts64DayRule"
									className="text-sm leading-5 text-[var(--ucm-text-dark)]"
								>
									Ich akzeptiere die 64-Tage-Regel für kurzfristige
									Beschäftigung <span className="text-red-500">*</span>
								</label>
							</div>
							<div className="flex items-center space-x-3">
								<input
									type="checkbox"
									id="acknowledgesDataAccuracy"
									checked={acknowledgesDataAccuracy}
									onChange={(e) =>
										setAcknowledgesDataAccuracy(e.target.checked)
									}
									className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
								/>
								<label
									htmlFor="acknowledgesDataAccuracy"
									className="text-sm leading-5 text-[var(--ucm-text-dark)]"
								>
									Ich bestätige, dass die obigen Angaben nach bestem Wissen
									korrekt sind. Bei Änderungen werde ich diese unverzüglich
									aktualisieren. <span className="text-red-500">*</span>
								</label>
							</div>
						</div>
					)}

					<div className="mt-8">
						<button
							onClick={handleSave}
							disabled={
								!validateForm() ||
								updateLoading ||
								(user?.internal_extra !== true &&
									(!formData.accepts_64_day_rule || !acknowledgesDataAccuracy))
							}
							className={`w-full h-14 font-semibold rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2 flex items-center justify-center ${
								validateForm() &&
								!updateLoading &&
								(user?.internal_extra === true ||
									(formData.accepts_64_day_rule && acknowledgesDataAccuracy))
									? "bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]"
									: "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed"
							}`}
						>
							{updateLoading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin mr-2" />
									{t("common.saving")}
								</>
							) : (
								t("common.continue")
							)}
						</button>
					</div>
				</div>
			</MainLayout>

			{/* Navigation Warning Modal */}
			{showNavigationWarningModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Änderungen speichern?
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Sie haben ungespeicherte Änderungen. Möchten Sie diese speichern,
							bevor Sie zur{" "}
							{navigationTarget === "work-permit-proof"
								? "Arbeitserlaubnis"
								: "Beschäftigungsnachweis"}
							-Upload Seite navigieren?
						</p>
						<div className="flex gap-3">
							<button
								onClick={() => setShowNavigationWarningModal(false)}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleSaveAndNavigate}
								disabled={updateLoading}
								className="flex-1 px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
							>
								{updateLoading ? (
									<span className="flex items-center justify-center">
										<Loader2 className="w-4 h-4 animate-spin mr-2" />
										Speichern...
									</span>
								) : (
									"Speichern und fortfahren"
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Occupation Change Confirmation Modal */}
			{showOccupationChangeModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Beschäftigung ändern
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Wenn Sie Ihre Beschäftigung ändern, wird Ihr vorhandener
							Beschäftigungsnachweis gelöscht. Sie müssen einen neuen
							Beschäftigungsnachweis für die ausgewählte Beschäftigung
							hochladen.
						</p>
						<div className="flex gap-3">
							<button
								onClick={handleCancelOccupationChange}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleConfirmOccupationChange}
								className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Änderung bestätigen
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Nationality Change Confirmation Modal */}
			{showNationalityChangeModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Nationalität ändern
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Wenn Sie Ihre Nationalität ändern, wird Ihre vorhandene
							Arbeitserlaubnis gelöscht.{" "}
							{pendingNationality &&
							countries.find((c) => c.nationality === pendingNationality)?.is_eu
								? "Als EU-Bürger benötigen Sie keine Arbeitserlaubnis mehr."
								: "Sie müssen eine neue Arbeitserlaubnis für die ausgewählte Nationalität hochladen."}
						</p>
						<div className="flex gap-3">
							<button
								onClick={handleCancelNationalityChange}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleConfirmNationalityChange}
								className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Änderung bestätigen
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ID Number Info Modal */}
			{showIdInfoModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Hinweis zur ID-Nummer
						</h2>
						<div className="space-y-4 text-[var(--ucm-text-medium)]">
							<p>
								Sie können hier jede gültige Ausweisnummer eingeben
								(Personalausweis oder Reisepass). Der deutsche Personalausweis
								wird jedoch bevorzugt.
							</p>
							<p className="font-medium">
								Wichtig: Bitte bringen Sie zum Probebetrieb genau das Dokument
								mit, dessen Nummer Sie hier eingeben. Dies wird zur
								Verifizierung benötigt.
							</p>
						</div>
						<div className="mt-6">
							<button
								onClick={() => setShowIdInfoModal(false)}
								className="w-full px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Verstanden
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
